import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { countryName } from "@/pages/admin/CountryPicker";

const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const where = (s) => (s === "all" ? "worldwide" : s.length > 6 ? `${s.length} countries` : s.map(countryName).join(", "));

function ClashRow({ c, i }) {
  const verdict = c.winner === "this" ? "This theme shows" : c.winner === "other" ? `“${c.name}” shows instead` : "Equal rank — whichever was added first shows";
  return (
    <li className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`theme-clash-${i}`}>
      <p className="font-semibold text-slate-800">“{c.name}” {!c.approved && <span className="text-xs font-bold text-amber-700">(draft)</span>}</p>
      <p className="text-slate-600">{fmt(c.starts_on)} – {fmt(c.ends_on)} · {where(c.shared)}</p>
      <p className={`font-bold ${c.winner === "this" ? "text-emerald-700" : "text-rf-orange"}`} data-testid={`theme-clash-verdict-${i}`}>{verdict}</p>
    </li>
  );
}

// Before saving, check for overlapping themes and let the admin Save anyway or go back to Edit.
export function useClashGuard() {
  const [state, setState] = useState(null);
  const guard = async (form, themeId, doSave) => {
    if (!form.starts_on || !form.ends_on) return doSave();
    try {
      const { data } = await api.post("/admin/seasonal-themes/clashes", { ...form, theme_id: themeId });
      if (!data.clashes.length) return doSave();
      setState({ clashes: data.clashes, doSave });
    } catch { toast.error("Couldn't check for clashes — saving anyway"); doSave(); }
  };
  const dialog = (
    <AlertDialog open={!!state} onOpenChange={(o) => !o && setState(null)}>
      <AlertDialogContent className="max-h-[85vh] overflow-y-auto" data-testid="theme-clash-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>This theme overlaps {state?.clashes.length === 1 ? "another theme" : `${state?.clashes.length} other themes`}</AlertDialogTitle>
          <AlertDialogDescription>On shared days, the theme aimed at the fewest countries shows; ties go to the shorter date range.</AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="space-y-2">{state?.clashes.map((c, i) => <ClashRow key={c.theme_id} c={c} i={i} />)}</ul>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="theme-clash-edit">Edit</AlertDialogCancel>
          <AlertDialogAction data-testid="theme-clash-save-anyway" onClick={() => { const s = state.doSave; setState(null); s(); }}>Save anyway</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { guard, dialog };
}
